import type { HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { Price as PriceTrans } from 'autogen/translation-keys/trans-website-product-screenreader';
import Text from 'components/generic/typography/components/Text';
import ScreenReaderOnly from 'components/ScreenReaderOnly';
import type { PriceModuleProps, Size } from './shared/types';

type PriceProps = {
    price: PriceModuleProps['price'];
    pricePrefix?: PriceModuleProps['pricePrefix'];
    isOnDiscount?: PriceModuleProps['isOnDiscount'];
    size: Size;
} & HTMLAttributes<HTMLDivElement>;

const PriceWrapper = styled.div<{ $isOnDiscount?: PriceProps['isOnDiscount'] }>`
    display: flex;
    align-items: baseline;
    gap: ${({ theme }) => theme.Spacing4};
    font-weight: ${({ theme }) => theme.TypographyBodyWeightStrong};

    ${({ $isOnDiscount, theme }) =>
        $isOnDiscount &&
        css`
            color: ${theme.ColorTextSale};
        `}
`;

export default function Price({ price, pricePrefix, size, isOnDiscount, ...restProps }: PriceProps) {
    const { className } = restProps;
    const { t } = useTranslation();
    const ariaLabel = `${t(PriceTrans)} ${pricePrefix ? `${pricePrefix} ${price}` : price}`;

    return (
        <PriceWrapper $isOnDiscount={isOnDiscount} className={className}>
            <ScreenReaderOnly>{ariaLabel}</ScreenReaderOnly>
            {pricePrefix && (
                <Text size={size === 'lg' ? 24 : 14} aria-hidden>
                    {pricePrefix}
                </Text>
            )}
            <Text size={size === 'lg' ? 24 : 16} aria-hidden>
                {price}
            </Text>
        </PriceWrapper>
    );
}
